import React, { useState } from "react";
import { Header, Icon, Accordion, Segment, Button, Divider } from "semantic-ui-react";
import "./InspectionItem.scss";
import { InspectionItemCommentCard } from "./InspectionItemCommentCard";
import { InspectionItemStatus, InspectionItemStatuses } from "./InspectionItem/InspectionItemStatus";
import { InspectionStatuses } from "./citizen-inspection-list/constants/InspectionStatus";
import InspectionItemPassFailButton from "./InspectionItem/InspectionItemPassFailButton";
import useDeleteInspectionItemStatus from "../../hooks/inspections/useDeleteInspectionItemStatus";
import { InspectionItemType } from "../../../queries/inspection-types/getInspectionType";
import useInspectionItemComments from "../../hooks/inspections/useInspectionItemComments";
import useInspectionItemPreviousComments from "../../hooks/inspections/useInspectionItemPreviousComments";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMessage } from "@fortawesome/pro-regular-svg-icons";
import { UserTypes } from "./InspectionItem/InspectionItemUserType";
import { ZonedDateTime } from "../../../../components/global_components/ZonedDateTime";

import { faClockRotateLeft } from "@fortawesome/pro-solid-svg-icons";

type Props = {
  title: any;
  inspectionStatus: any;
  itemStatus: any;
  previousItemStatus: InspectionItemStatus | null;
  previousItemCompletedDate: string | null;
  itemId: any;
  sectionId: any;
  inspectionId: any;
  // TODO: replace this with the type I defined and propagate it up to the parent components. Its called "InspectionItemUserType"
  user: "citizen" | "staff";
  onPass?: (id: string, sectionId: string, itemId: string) => Promise<void>;
  onFail?: (id: string, sectionId: string, itemId: string) => Promise<void>;
  itemType?: InspectionItemType;
};
export const InspectionItem = (props: Props) => {
  const {
    title,
    inspectionStatus,
    itemStatus,
    itemId,
    sectionId,
    inspectionId,
    user,
    onPass,
    onFail,
    itemType,
    previousItemStatus,
    previousItemCompletedDate,
  } = props;

  // TODO: This state is weird - I'd like to get rid of it in favor of the useMutation loading state, just not right now - Peter
  const [loadingItem, setLoadingItem] = useState<"pass" | "fail" | "">("");
  const [active, setActive] = useState(false);
  const completeInspection =
    inspectionStatus == InspectionStatuses.PASSED || inspectionStatus == InspectionStatuses.FAILED;
  const toggleActive = () => setActive((prevActive) => !prevActive);

  const isPublicView = user === "citizen";

  const conversationQuery = useInspectionItemComments({
    inspectionId: inspectionId,
    sectionId: sectionId,
    itemId: itemId,
  });
  const previousConversationQuery = useInspectionItemPreviousComments({
    inspectionId: inspectionId,
    sectionId: sectionId,
    itemId: itemId,
    options: {
      enabled: !isPublicView,
    },
  });

  const previousComments = previousConversationQuery.data?.data ?? [];
  const comments = conversationQuery.data?.data ?? [];

  const latestComment =
    comments.length !== 0
      ? comments[comments.length - 1]
      : previousComments.length !== 0
      ? previousComments[previousComments.length - 1]
      : null;
  const handlePass = () => {
    setLoadingItem("pass");
    if (onPass) {
      onPass(inspectionId, sectionId, itemId);
    }
  };

  const handleFail = () => {
    setLoadingItem("fail");
    if (onFail) {
      onFail(inspectionId, sectionId, itemId);
    }
  };

  const { resetInspectionItemStatus, isLoading: resetIsLoading } = useDeleteInspectionItemStatus();
  const handleReset = (ev: React.MouseEvent) => {
    ev.stopPropagation();
    setLoadingItem("");
    resetInspectionItemStatus({
      id: inspectionId,
      sectionId: sectionId,
      itemId: itemId,
    });
  };

  const isFailLoading = loadingItem === "fail";
  const isPassLoading = loadingItem === "pass";
  const passed = itemStatus === InspectionItemStatuses.PASSED;
  const failed = itemStatus === InspectionItemStatuses.FAILED;

  const description = itemType?.description;

  return (
    <Accordion
      styled
      fluid
      className='inspection-item'
      // TODO: Tailwind
      style={{ borderTop: passed ? "4px solid #067E30" : failed ? "4px solid #DF0202" : "" }}
    >
      <Accordion.Title active={active} onClick={toggleActive}>
        <div className='tw-flex tw-justify-between'>
          <Header className='tw-pt-2' as='h5'>
            <Button compact className='inline-text-button' aria-expanded={active}>
              <Icon name='dropdown' className='visible' />
              {title}
            </Button>
          </Header>
          <div className='pass-fail-buttons'>
            {inspectionStatus === InspectionStatuses.STARTED && user === UserTypes.STAFF && (
              <Button
                disabled={!passed && !failed}
                loading={resetIsLoading}
                onClick={handleReset}
                className='button-ghost'
              >
                Reset
              </Button>
            )}
            {inspectionStatus === InspectionStatuses.STARTED && (
              <InspectionItemPassFailButton
                user={user}
                status={itemStatus}
                isInspectionComplete={!!completeInspection}
                onFail={handleFail}
                isFailLoading={isFailLoading}
                onPass={handlePass}
                isPassLoading={isPassLoading}
              />
            )}
          </div>
        </div>
        {(previousItemStatus || latestComment) && !isPublicView && <Divider />}
        <div className='tw-flex'>
          {previousItemStatus && !isPublicView && (
            <div
              className={`tw-text-baseline tw-m-0 tw-mr-6 tw-flex tw-pb-2 tw-pl-2 tw-align-middle tw-font-medium ${
                previousItemStatus === InspectionItemStatuses.FAILED
                  ? "tw-text-[#db2828]"
                  : previousItemStatus === InspectionItemStatuses.PASSED
                  ? "tw-text-[#067e30]"
                  : ""
              }`}
            >
              {previousItemStatus ? (
                <div className='tw-mr-1 tw-flex tw-rounded'>
                  <div className='tw-m-0.25'>
                    <FontAwesomeIcon icon={faClockRotateLeft} />
                  </div>
                </div>
              ) : undefined}
              <div className='tw-mr-6 tw-px-1'>
                {previousItemStatus === InspectionItemStatuses.PASSED ? "Passed" : "Failed "}
              </div>
              <div className='tw-mr-6 tw-font-normal'>
                <ZonedDateTime value={previousItemCompletedDate} formatString='EEE, LLL d, y h:mm a' />
              </div>
            </div>
          )}
          {latestComment && !isPublicView && (
            <div className='tw-flex tw-font-normal tw-text-[#000000B2]'>
              <FontAwesomeIcon icon={faMessage} className='tw-mr-1 tw-pt-0.5' />
              {latestComment.comment ||
                (latestComment.attachment_ids && latestComment.attachment_ids.length > 0 ? "(Photo attached)" : "")}
            </div>
          )}
        </div>
      </Accordion.Title>
      <Accordion.Content active={active} className='tw-p-0'>
        <Segment secondary className='tw-rounded-b-[4px] tw-border-none tw-bg-white tw-p-0'>
          {description && (
            <div className='tw-mx-4'>
              {description && <label> Description </label>}
              <p className='tw-pb-2'>{description ?? ""}</p>
            </div>
          )}
          <InspectionItemCommentCard
            inspectionId={inspectionId}
            sectionId={sectionId}
            itemId={itemId}
            isStaff={user === "staff"}
            isInspectionComplete={completeInspection}
            isPublicView={isPublicView}
          />
        </Segment>
      </Accordion.Content>
    </Accordion>
  );
};
